/**
 * Change App HTTP Client
 *
 * Dedicated Axios instance for the BFF change-app endpoints. Operates
 * independently from the portal's BFF client (no interceptors); callers pass
 * the `Authorization` header explicitly.
 *
 * ## Endpoints
 * - `POST /auth/change-app` — Obtain minitoken
 * - `POST /auth/renew-app-token` — Exchange minitoken for an access token
 * - `GET /auth/user-info` — Fetch authenticated user data
 *
 * @since 1.0.0
 * @category Services/ChangeApp
 */

import axios from 'axios';
import type { AxiosError } from 'axios';

import { ChangeAppError } from './errors';
import type { ApiErrorItem } from './types';

/** Axios instance dedicated to change-app operations */
const iamClient = axios.create({
  headers: { 'Content-Type': 'application/json' },
});

/**
 * Parse error items from an IAM error body, either raw or wrapped by the
 * BFF exception filter as `{ statusCode, message: <IAM body> }`.
 *
 * @param data - Raw response data
 * @returns Array of structured error items
 */
function parseErrorItems(data: unknown): ApiErrorItem[] {
  if (Array.isArray(data)) return data as ApiErrorItem[];
  if (data && typeof data === 'object' && 'code' in data && 'message' in data) {
    return [data as ApiErrorItem];
  }
  if (data && typeof data === 'object' && 'statusCode' in data) {
    const wrapped = (data as { message?: unknown }).message;
    if (wrapped && typeof wrapped === 'object') return parseErrorItems(wrapped);
  }
  return [];
}

/**
 * Transform Axios errors into ChangeAppError instances
 *
 * @param error - Caught error from Axios request
 * @throws {ChangeAppError} When the error is an Axios error
 * @throws Re-throws non-Axios errors as-is
 */
function handleAxiosError(error: unknown): never {
  if (axios.isAxiosError(error)) {
    const axiosErr = error as AxiosError;
    const status = axiosErr.response?.status ?? 0;
    const data = axiosErr.response?.data;
    const errors = parseErrorItems(data);
    const firstError = errors[0];

    throw new ChangeAppError(
      firstError?.message ?? axiosErr.message ?? 'Request failed',
      status,
      firstError?.code ?? String(status),
      errors
    );
  }
  throw error;
}

/**
 * Perform an authenticated POST request to the IAM API
 *
 * @param url - Full request URL
 * @param body - Request body payload
 * @param headers - Extra request headers (including `Authorization`)
 * @returns Parsed response data
 * @throws {ChangeAppError} On API failure
 *
 * @since 1.0.0
 * @category Services/ChangeApp
 */
export async function httpPost<T>(
  url: string,
  body: Record<string, unknown> | undefined,
  headers: Record<string, string>
): Promise<T> {
  try {
    const response = await iamClient.post<T>(url, body, { headers });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}

/**
 * Perform an authenticated GET request to the IAM/BFF API
 *
 * @param url - Full request URL
 * @param headers - Extra request headers (including `Authorization`)
 * @returns Parsed response data
 * @throws {ChangeAppError} On API failure
 *
 * @since 1.0.0
 * @category Services/ChangeApp
 */
export async function httpGet<T>(
  url: string,
  headers: Record<string, string>
): Promise<T> {
  try {
    const response = await iamClient.get<T>(url, { headers });
    return response.data;
  } catch (error) {
    handleAxiosError(error);
  }
}
